import { formatDateLong } from '@obras/shared';

import { PageHeader } from '../components/AppShell.js';
import { Stagger, StaggerItem } from '../components/motion.js';
import { Card, CardHeader } from '../components/ui/Card.js';
import { ErrorState, RefreshingBar, Skeleton } from '../components/ui/Feedback.js';
import { usePainel } from '../features/painel/api.js';
import {
  AtividadeRecente,
  CardLink,
  ExecucaoFinanceira,
  MedicoesPorMes,
  NoObras,
  ObrasPorSituacao,
  PrecisaDeAtencao,
} from '../features/painel/secoes.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';

/** Com a forma real da tela: as três faixas de cartões. */
function PainelEsqueleto() {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3" aria-busy>
      <Skeleton className="h-[264px] rounded-none xl:col-span-2" />
      <Skeleton className="h-[264px] rounded-none" />
      <Skeleton className="h-[420px] rounded-none xl:col-span-3" />
      <Skeleton className="h-[428px] rounded-none xl:col-span-2" />
      <Skeleton className="h-[428px] rounded-none" />
    </div>
  );
}

/**
 * Painel do órgão (design.md §8, Painel).
 *
 * Cinco cartões em três faixas, na ordem das perguntas de quem gere: **quanto
 * há** — a execução financeira, no anel, e as obras por situação —; **o que
 * pede ação hoje** — os motivos e as obras, da mais urgente —; e **como anda**
 * — as medições por mês e os últimos relatórios. Tudo apurado no servidor,
 * numa leitura só; o detalhe de cada assunto fica na tela dele.
 *
 * Os cartões entram escalonados, de cima para baixo, e os gráficos se
 * desenham ao chegar: o anel, os números, as barras e as colunas. Só na
 * primeira pintura — a revalidação não remonta nada.
 */
export function DashboardPage() {
  const { organization, may, user } = useAuth();
  const { data, isPending, isFetching, isError, error, refetch } = usePainel();

  /*
   * No painel do fiscal os números são da carteira dele; os atalhos para a
   * lista de obras levam o mesmo recorte, para o total de lá bater com o daqui.
   */
  const carteira = data?.scope === 'carteira' ? (user?.id ?? null) : null;

  const description = data
    ? data.scope === 'carteira'
      ? `Obras sob sua fiscalização em ${formatDateLong(data.today)}.`
      : `Posição do acervo de ${organization?.name ?? 'seu órgão'} em ${formatDateLong(data.today)}.`
    : 'Posição do acervo, apurada no servidor.';

  function conteudo() {
    if (isError) {
      return (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      );
    }

    if (isPending) return <PainelEsqueleto />;

    if (data.totals.obras === 0) {
      return (
        <Card square>
          <NoObras scope={data.scope} />
        </Card>
      );
    }

    return (
      <Stagger
        className={cn(
          'grid grid-cols-1 gap-4 transition-opacity duration-200 xl:grid-cols-3',
          isFetching && 'opacity-60',
        )}
      >
        <StaggerItem className="xl:col-span-2">
          <Card square className="h-full">
            <ExecucaoFinanceira totals={data.totals} />
          </Card>
        </StaggerItem>

        <StaggerItem>
          <Card square className="h-full">
            <ObrasPorSituacao totals={data.totals} scope={data.scope} fiscalId={carteira} />
          </Card>
        </StaggerItem>

        {/*
          O fiscal não é parte contratada: é o servidor designado para
          fiscalizar a execução do contrato. Por isso cada obra traz o "fiscal
          responsável" — quem responde pela pendência.
        */}
        <StaggerItem className="xl:col-span-3">
          <Card square>
            <PrecisaDeAtencao
              alerts={data.alerts}
              attention={data.attention}
              scope={data.scope}
              fiscalId={carteira}
            />
          </Card>
        </StaggerItem>

        <StaggerItem className="xl:col-span-2">
          <Card square className="h-full">
            <CardHeader
              title="Medições por mês"
              description="Pago em cada um dos últimos doze meses, pela data do ato."
            />
            <MedicoesPorMes months={data.months} today={data.today} />
          </Card>
        </StaggerItem>

        <StaggerItem>
          <Card square className="h-full">
            <CardHeader
              title="Atividade recente"
              description="Últimos relatórios publicados."
              action={
                may('canAccessReports') ? (
                  <CardLink to="/relatorios">Ver relatórios</CardLink>
                ) : undefined
              }
            />
            <AtividadeRecente rows={data.activity} />
          </Card>
        </StaggerItem>
      </Stagger>
    );
  }

  return (
    <>
      <PageHeader title="Painel" description={description} />

      {/* Revalidação não desmonta o conteúdo: só sinaliza com a barra fina. */}
      <div className="mb-4">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {conteudo()}
    </>
  );
}
